/* eslint-disable no-console */
/**
 * @file useForm.js
 * @description Enhanced form validation composable with nested schema support
 * @version 2.0.0
 */

import {
  computed,
  nextTick,
  reactive,
  ref,
  shallowReactive,
  toRaw,
  watch,
  watchEffect,
} from 'vue';
import {
  deepClone,
  flattenValidationSchema,
  getAllFieldPaths,
  getNestedValue,
  isDeepEqual,
  setNestedValue,
} from './utils';

/**
 * @template TForm
 * @typedef {Object} ValidationContext
 * @property {boolean} isSequentialValidation
 * @property {Partial<TForm>} previousValues
 * @property {Set<string>} validatedFields
 * @property {Function} getFieldValue
 * @property {Function} getFieldError
 * @property {Function} isFieldValid
 */

/**
 * @template TForm
 * @typedef {Object} UseFormOptions
 * @property {TForm} initialValues - Initial form values
 * @property {Object} validationSchema - Validation rules (can be nested to mirror form structure)
 * @property {Function} onSubmit - Callback for successful submission
 * @property {Function} [onInvalid] - Callback for failed validation
 * @property {boolean} [sequentialValidation=false] - Enable sequential validation mode
 * @property {Object} [onChange] - Field change handlers
 * @property {Object} [computed] - Computed field definitions
 */

/**
 * @template TForm
 * @typedef {Object} UseFormReturn
 * @property {TForm} form - Reactive form state
 * @property {Object} errors - Field validation errors
 * @property {Object} touched - Field touched state
 * @property {import('vue').ComputedRef<boolean>} isValid - Form is valid (no errors)
 * @property {Function} validateField - Validate a single field (also sets touched)
 * @property {Function} validateAll - Validate all fields
 * @property {Function} validateSequentially - Validate fields sequentially
 * @property {Function} setTouched - Explicitly mark field as touched
 * @property {Function} resetForm - Reset form to initial values
 * @property {Function} submit - Submit form with validation
 * @property {import('vue').Ref<boolean>} isSubmitting - Submission state
 * @property {import('vue').ComputedRef<boolean>} isValidating - Validation state
 * @property {Function} setFieldValue - Set field value with options
 * @property {Function} setFieldValueSync - Set field value with auto-validation
 * @property {Function} resetField - Reset single field
 * @property {Function} setFieldError - Set custom error
 * @property {Function} clearFieldError - Clear field error
 * @property {Function} clearValue - Clear field value
 * @property {Function} getFieldValue - Get current field value
 * @property {import('vue').Ref<TForm>} initialValues - Initial values ref
 * @property {import('vue').ComputedRef<boolean>} hasChanges - Has changes computed
 */

/**
 * Enhanced useForm composable with nested schema support and Vue watch style onChange
 *
 * @template {Record<string, any>} TForm
 * @param {UseFormOptions<TForm>} options - Configuration options
 * @returns {UseFormReturn<TForm>} Form methods and reactive state
 *
 * @example
 * const { form, errors, touched, validateField, submit } = useForm({
 *   initialValues: {
 *     email: '',
 *     password: '',
 *     user: { profile: { name: '' } }
 *   },
 *   validationSchema: {
 *     email: all(
 *       validators.required('Email required'),
 *       validators.email()
 *     ),
 *     password: validators.minLength(8),
 *     user: {
 *       profile: {
 *         name: validators.required()
 *       }
 *     }
 *   },
 *   onSubmit: async (formData) => {
 *     await api.save(formData);
 *   },
 *   onInvalid: ({ invalidFields, errors }) => {
 *     console.log('Invalid fields:', invalidFields);
 *   }
 * });
 *
 * // Usage in template:
 * // <input v-model="form.email" @blur="validateField('email')" />
 * // <span v-if="touched.email && errors.email">{{ errors.email }}</span>
 */
export function useForm(options) {
  const {
    initialValues,
    validationSchema = {},
    onSubmit,
    onInvalid,
    sequentialValidation = false,
    onChange,
    computed: computedFields,
  } = options;

  const isSubmitting = ref(false);
  const activeValidations = ref(0);
  const isValidating = computed(() => activeValidations.value > 0);

  // State management
  const form = reactive(deepClone(initialValues));
  const frozenInitialValues = ref(deepClone(initialValues));
  const previousFormRef = ref(deepClone(initialValues));
  let isInternalUpdate = false;

  // Computed property to detect if form has changes
  const hasChanges = computed(() => {
    return !isDeepEqual(form, frozenInitialValues.value);
  });

  // Flatten validation schema for easier lookup
  const flatValidationSchema = flattenValidationSchema(validationSchema);

  // Get all possible field paths
  const allFieldPaths = getAllFieldPaths(initialValues);

  // Initialize errors and touched with all field paths upfront for Vue 2 reactivity
  // Use shallowReactive for better performance (flat objects don't need deep tracking)
  const errors = shallowReactive(
    Object.fromEntries(
      [...allFieldPaths, ...Object.keys(flatValidationSchema)].map(path => [
        path,
        '',
      ])
    )
  );
  const touched = shallowReactive(
    Object.fromEntries(
      [...allFieldPaths, ...Object.keys(flatValidationSchema)].map(path => [
        path,
        false,
      ])
    )
  );
  const validatedFields = new Set();

  // Computed to check if form is valid (no errors with actual error messages)
  const isValid = computed(() => {
    return !Object.keys(errors).some(key => errors[key] !== '');
  });

  // Lazy initialization helper - only initialize when field is accessed
  function ensureFieldState(fieldPath) {
    if (!(fieldPath in errors)) {
      errors[fieldPath] = '';
    }
    if (!(fieldPath in touched)) {
      touched[fieldPath] = false;
    }
  }

  const getFieldValue = fieldPath => {
    return getNestedValue(form, fieldPath);
  };

  // Validation context for validators
  const validationContext = computed(() => ({
    isSequentialValidation: sequentialValidation,
    previousValues: previousFormRef.value,
    validatedFields: new Set(validatedFields),
    getFieldValue,
    getFieldError: fieldPath => errors[fieldPath] || '',
    isFieldValid: fieldPath =>
      !errors[fieldPath] && validatedFields.has(fieldPath),
  }));

  // Setup computed fields with batched updates
  if (computedFields) {
    const pendingUpdates = new Map();
    let updateScheduled = false;

    const flushUpdates = () => {
      if (pendingUpdates.size > 0 && !isInternalUpdate) {
        isInternalUpdate = true;
        pendingUpdates.forEach((value, fieldName) => {
          form[fieldName] = value;
        });
        pendingUpdates.clear();
        isInternalUpdate = false;
      }
      updateScheduled = false;
    };

    Object.entries(computedFields).forEach(([computedFieldName, computeFn]) => {
      watchEffect(() => {
        const value = computeFn(form);
        pendingUpdates.set(computedFieldName, value);

        if (!updateScheduled) {
          updateScheduled = true;
          nextTick(flushUpdates);
        }
      });
    });
  }

  // Setup onChange watchers with infinite loop prevention
  if (onChange) {
    // Field-specific handlers
    Object.entries(onChange).forEach(([fieldName, handler]) => {
      if (fieldName === '$all' || typeof handler !== 'function') return;

      watch(
        () => getNestedValue(form, fieldName),
        async (newValue, oldValue) => {
          if (isInternalUpdate || isDeepEqual(newValue, oldValue)) return;

          try {
            await handler(newValue, form);
          } catch (error) {
            // eslint-disable-next-line no-console
            console.error(
              `onChange handler error for field ${fieldName}:`,
              error
            );
          }
        }
      );
    });

    // Global $all handler with Vue watch style
    if (onChange.$all) {
      watch(
        () => toRaw(form),
        async newForm => {
          if (isInternalUpdate) return;

          const prevForm = previousFormRef.value;
          if (isDeepEqual(newForm, prevForm)) return;

          try {
            await onChange.$all(newForm, prevForm);
            previousFormRef.value = deepClone(newForm);
          } catch (error) {
            console.error('$all onChange handler error:', error);
          }
        },
        { deep: true, flush: 'post' }
      );
    }
  }

  /**
   * Validates a single field and marks it as touched
   *
   * @param {string} fieldPath - Field path (e.g., 'email' or 'user.profile.name')
   * @returns {Promise<boolean>} Returns true if validation passed
   *
   * @example
   * // Simple field
   * await validateField('email')
   *
   * // Nested field
   * await validateField('user.profile.name')
   *
   * // Use in template
   * // <input @blur="validateField('email')" />
   */
  const validateField = async fieldPath => {
    ensureFieldState(fieldPath);
    touched[fieldPath] = true;
    errors[fieldPath] = '';
    activeValidations.value += 1;

    try {
      const validator = flatValidationSchema[fieldPath];
      if (validator) {
        const context = validationContext.value;
        const fieldValue = getFieldValue(fieldPath);
        const result = await validator(fieldValue, form, context);

        if (result !== true) {
          errors[fieldPath] = result;
          validatedFields.delete(fieldPath);
          return false;
        }

        validatedFields.add(fieldPath);
        return true;
      }

      validatedFields.add(fieldPath);
      return true;
    } catch (error) {
      console.error(`Validation error for field ${fieldPath}:`, error);
      errors[fieldPath] = 'Validation failed';
      validatedFields.delete(fieldPath);
      return false;
    } finally {
      activeValidations.value -= 1;
    }
  };

  const validateAll = async () => {
    const validationPromises = [];
    const fieldsToValidate = Object.keys(flatValidationSchema);

    fieldsToValidate.forEach(fieldPath => {
      touched[fieldPath] = true;
      validationPromises.push(validateField(fieldPath));
    });

    const results = await Promise.all(validationPromises);
    return results.every(result => result);
  };

  const validateSequentially = async (fieldOrder, opts) => {
    const fieldsToValidate = fieldOrder || Object.keys(flatValidationSchema);
    let allValid = true;

    // eslint-disable-next-line no-restricted-syntax
    for (const fieldPath of fieldsToValidate) {
      touched[fieldPath] = true;
      // eslint-disable-next-line no-await-in-loop
      const fieldIsValid = await validateField(fieldPath);

      if (!fieldIsValid) {
        allValid = false;
        if (opts?.stopOnError) {
          return false;
        }
      }
    }

    return allValid;
  };

  /**
   * Explicitly mark a field as touched without validating
   * Useful for showing validation on focus or custom triggers
   *
   * @param {string} fieldPath - Field path to mark as touched
   * @param {boolean} [value=true] - Touched state (true or false)
   *
   * @example
   * // Mark as touched on focus
   * setTouched('email')
   *
   * // Mark as untouched
   * setTouched('email', false)
   *
   * // Use in template
   * // <input @focus="setTouched('email')" />
   */
  const setTouched = (fieldPath, value = true) => {
    ensureFieldState(fieldPath);
    touched[fieldPath] = value;
  };

  const resetForm = newInitialValues => {
    const baseState = deepClone(frozenInitialValues.value);
    const stateToResetTo = { ...baseState, ...newInitialValues };

    isInternalUpdate = true;
    Object.assign(form, stateToResetTo);
    isInternalUpdate = false;

    // Clear errors and touched state
    allFieldPaths.forEach(path => {
      errors[path] = '';
      touched[path] = false;
    });

    validatedFields.clear();
    previousFormRef.value = deepClone(stateToResetTo);
    isSubmitting.value = false;
  };

  /**
   * Update initial values and sync form state
   * Useful after fetching data or successful save
   */
  const setInitialValues = newValues => {
    const clonedValues = deepClone(newValues);
    frozenInitialValues.value = clonedValues;
    Object.assign(form, clonedValues);
  };

  const resetField = fieldPath => {
    const initialValue = getNestedValue(frozenInitialValues.value, fieldPath);

    isInternalUpdate = true;
    setNestedValue(form, fieldPath, initialValue);
    isInternalUpdate = false;

    ensureFieldState(fieldPath);
    errors[fieldPath] = '';
    touched[fieldPath] = false;
    validatedFields.delete(fieldPath);
  };

  const setFieldValue = async (fieldPath, value, opts = {}) => {
    if (opts.silent) {
      isInternalUpdate = true;
    }

    setNestedValue(form, fieldPath, value);

    if (opts.silent) {
      isInternalUpdate = false;
    }
  };

  const setFieldValueSync = async (fieldPath, value) => {
    await setFieldValue(fieldPath, value, { shouldValidate: true });
  };

  const setFieldError = (fieldPath, error) => {
    ensureFieldState(fieldPath);
    errors[fieldPath] = error;
    validatedFields.delete(fieldPath);
  };

  const clearFieldError = fieldPath => {
    ensureFieldState(fieldPath);
    errors[fieldPath] = '';
  };

  const clearValue = fieldPath => {
    const initialValue = getNestedValue(frozenInitialValues.value, fieldPath);

    isInternalUpdate = true;
    setNestedValue(form, fieldPath, initialValue);
    isInternalUpdate = false;

    ensureFieldState(fieldPath);
    errors[fieldPath] = '';
    touched[fieldPath] = false;
    validatedFields.delete(fieldPath);
  };

  const submit = async () => {
    isSubmitting.value = true;

    try {
      const canSubmit = sequentialValidation
        ? await validateSequentially()
        : await validateAll();

      if (canSubmit) {
        const formData = toRaw(form);
        await onSubmit?.(formData);
      } else {
        const invalidFields = Object.keys(errors).filter(key => errors[key]);

        onInvalid?.({
          invalidFields,
          errors: toRaw(errors),
        });
      }
    } catch (error) {
      console.error('Form submission error:', error);
      throw error;
    } finally {
      isSubmitting.value = false;
    }
  };

  return {
    form,
    initialValues: frozenInitialValues,
    hasChanges,
    errors,
    touched,
    isValid,
    validateField,
    validateAll,
    validateSequentially,
    setTouched,
    resetForm,
    setInitialValues,
    submit,
    isSubmitting,
    isValidating,
    setFieldValue,
    setFieldValueSync,
    resetField,
    setFieldError,
    clearFieldError,
    clearValue,
    getFieldValue,
  };
}
